import {
  Component,
  effect,
  inject,
  OnInit,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AppService } from '@shared/services/app.service';
import { Tabla } from '@shared/components/tabla/tabla';
import { ITablaColumna } from '@core/interfaces/tabla.interface';
import { IPaginar } from '@core/interfaces/paginacion/paginacion.interface';
import { EmpleadoApiService } from '@core/services/api/empleado-api.service';
import { IEmpleadoData } from '@core/interfaces/entidades/empleado.entidad';
import { IOptionsSelect } from '@core/interfaces/options.select.interface';
import { ToastService } from '@shared/services/toast.service';
import { FormField, form, required } from '@angular/forms/signals';
import { FormsModule } from '@angular/forms';
import { EAtajoRangoTiempo } from '@core/enums/atajoRangoTiempo.enum';
import { calcularAtajoRangoFecha } from '@shared/utils/functions.utils';
import { CajaTurnoApiService } from '@core/services/api/caja-turno.service';
import {
  IReporteCajaFiltroReq,
  IReporteCajaRes,
  IResumenReporteCajaRes,
} from '@core/interfaces/request-response/caja.dto.interface';
import { FormErrorMessagePipe } from '@shared/pipes/form-error-message.pipe';
import { Paginacion } from '@shared/components/paginacion/paginacion';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { environment } from '@env/environment';
import { FiltrosReporte } from '@shared/components/filtros-reporte/filtros-reporte';
import { SucursalService } from '@shared/services/sucursal.service';
import { ISucursalData } from '@core/interfaces/entidades/sucursal.entidad';
import { NombreSucursalPipe } from '../../../shared/pipes/nombre-sucursal.pipe';
import { descargarArchivoDesdeResponse } from '@shared/utils/functions.utils';
import { UiDatepicker, UiDialog, UiSelect, UiTooltip } from '@shared/components/ui';

@Component({
  selector: 'app-reporte-caja',
  imports: [
    Tabla,
    FormField,
    FormErrorMessagePipe,
    Paginacion,
    DatePipe,
    CurrencyPipe,
    FiltrosReporte,
    NombreSucursalPipe,
    FormsModule,
    UiDatepicker,
    UiDialog,
    UiSelect,
    UiTooltip,
  ],
  templateUrl: './reporte-caja.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './reporte-caja.css',
})
export class ReporteCaja implements OnInit {
  constructor() {
    effect(() => {
      if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
        this.cargarEmpleados();
        this.cargarOpcionesSucursal();
      }
    });
  }
  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Reporte de Caja',
      subtitle: 'Visualiza el reporte de caja',
      showBackButton: true,
      icon: 'bi bi-cash-stack',
    });
    this.columnasTabla = [
      {
        key: 'sucursal_id',
        titulo: 'Sucursal',
        template: this.sucursalColumnTemplate(),
      },
      {
        key: 'hora_apertura',
        titulo: 'Fecha Apertura',
        pipe: new DatePipe(environment.locale),
      },
      {
        key: 'monto_apertura',
        titulo: 'Monto Apertura',
        pipe: new CurrencyPipe(environment.locale, 'PEN'),
      },
      { key: 'hora_cierre', titulo: 'Fecha Cierre', pipe: new DatePipe(environment.locale) },
      {
        key: 'monto_cierre_efectivo',
        titulo: 'Efectivo esperado',
        pipe: new CurrencyPipe(environment.locale, 'PEN'),
      },
      {
        key: 'monto_cierre_reportado',
        titulo: 'Efectivo entregado',
        pipe: new CurrencyPipe(environment.locale, 'PEN'),
      },
      {
        key: 'diferencia',
        titulo: 'Diferencia',
        template: this.diferenciaTemplate(),
      },
      // {
      //   key: 'total_ventas_brutas',
      //   titulo: 'Ventas realizadas',
      //   pipe: new CurrencyPipe(environment.locale, 'PEN'),
      // },
      // {
      //   key: 'total_egresos',
      //   titulo: 'Egresos',
      //   pipe: new CurrencyPipe(environment.locale, 'PEN'),
      // },
      // {
      //   key: 'monto_cierre_sistema',
      //   titulo: 'Total esperado (sistema)',
      //   pipe: new CurrencyPipe(environment.locale, 'PEN'),
      // },
      { key: 'usuario_nombre_completo', titulo: 'Empleado' },
      { key: 'cerrada', titulo: 'Estado', template: this.estadoTemplate() },
    ];
  }
  // Servicios
  private appService = inject(AppService);
  private empleadoApiService = inject(EmpleadoApiService);
  private toastService = inject(ToastService);
  private cajaTurnoApiService = inject(CajaTurnoApiService);
  private sucursalService = inject(SucursalService);
  canSelectSucursal = this.sucursalService.canSelectSucursal;

  private itemsPorPagina = 18;
  // templates
  estadoTemplate = viewChild('estadoTemplate');
  diferenciaTemplate = viewChild('diferenciaTemplate');
  // Datos
  maxDate = new Date(new Date().setHours(23, 59, 59, 999));
  minDate = new Date(new Date().setFullYear(new Date().getFullYear() - 1));
  columnasTabla: ITablaColumna[] = [];
  paginacion = signal<IPaginar<IReporteCajaRes> | null>(null);
  resumenReporte = signal<IResumenReporteCajaRes | null>(null);

  modal_ver_movimientos_abiertos = signal(false);
  movimiento_seleccionado: IReporteCajaRes | null = null;

  private requestFiltro: IReporteCajaFiltroReq | null = null;
  // filtros
  optionesEmpleado = signal<IOptionsSelect<IEmpleadoData | null>[]>([]);
  opcionesSucursal = signal<IOptionsSelect<ISucursalData | null>[]>([]);

  formFiltro = form(
    signal({
      rangoFecha: null as Date[] | null,
      empleado: null as IEmpleadoData | null,
      estadoCaja: null as boolean | null,
      sucursal: null as ISucursalData | null,
    }),
    (x) => {
      required(x.rangoFecha, { message: 'El rango de fechas es obligatorio' });
    },
  );
  // métodos
  private checkInvalidForm(): boolean {
    if (this.formFiltro().invalid()) {
      this.markAllAsTouched();
      return true;
    }
    return false;
  }
  private markAllAsTouched() {
    this.formFiltro.rangoFecha().markAsTouched();
  }
  EAtajoRangoTiempo = EAtajoRangoTiempo;
  atajoRangoFecha(tiempo: EAtajoRangoTiempo) {
    const { inicio, fin } = calcularAtajoRangoFecha(tiempo);
    this.formFiltro.rangoFecha().value.set([inicio, fin]);
  }
  aplicarFiltros($event: Event) {
    $event.preventDefault();
    if (this.checkInvalidForm()) return;
    const rq = this.buildRequestFiltro();
    if (!rq) return;

    this.requestFiltro = rq;
    this.cambiarPagina(1);
  }

  exportarExcel() {
    if (this.checkInvalidForm()) return;
    const rq = this.buildRequestFiltro();
    if (!rq) return;

    this.cajaTurnoApiService.exportarReporteExcel(rq).subscribe({
      next: (response) => {
        descargarArchivoDesdeResponse(response, 'reporte-caja-turno.xlsx');
        this.toastService.show('top-right', 'success', 'Excel', 'Reporte de caja descargado.');
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudo exportar el reporte de caja.',
        );
      },
    });
  }

  private buildRequestFiltro(): IReporteCajaFiltroReq | null {
    const { rangoFecha, empleado, estadoCaja, sucursal } = this.formFiltro().controlValue();
    if (!rangoFecha || rangoFecha.length !== 2) return null;

    return {
      fecha_inicio: rangoFecha[0],
      fecha_fin: rangoFecha[1],
      sucursal_id: sucursal ? sucursal.id : undefined,
      empleado_id: empleado ? empleado.id : undefined,
      estado_caja: estadoCaja !== null ? estadoCaja : undefined,
    };
  }
  limpiarFiltros() {
    this.formFiltro.rangoFecha().value.set([]);
    this.formFiltro.empleado().value.set(null);
    this.formFiltro.estadoCaja().value.set(null);
    if (this.canSelectSucursal()) {
      this.formFiltro.sucursal().value.set(null);
    }
  }
  generarReporte() {}

  cambiarPagina(pagina: number) {
    if (!this.requestFiltro) return;
    this.cajaTurnoApiService
      .paginarReporte(this.requestFiltro, pagina, this.itemsPorPagina)
      .subscribe({
        next: (rs) => {
          const { paginar, resumen } = rs;
          this.paginacion.set({
            ...paginar,
            pagina: pagina,
          });
          this.resumenReporte.set(resumen);
        },
        error: () => {
          this.toastService.show(
            'top-right',
            'error',
            'Error',
            'No se pudo cargar el reporte de caja con los filtros aplicados',
          );
        },
      });
  }
  verDetallesMovimiento(row: IReporteCajaRes) {
    this.movimiento_seleccionado = row;
    this.modal_ver_movimientos_abiertos.set(true);
  }

  // cargas API
  private cargarEmpleados() {
    this.empleadoApiService.cargar().subscribe({
      next: (empleados) => {
        this.optionesEmpleado.set(
          empleados.map((empleado) => ({
            label: empleado.nombre_completo,
            value: empleado,
          })),
        );
        // agregamos opción de todos
        this.optionesEmpleado.update((options) => [
          {
            label: 'Todos los empleados',
            value: null,
          },
          ...options,
        ]);
      },
      error: () => {
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          'No se pudieron cargar los empleados para el filtro',
        );
      },
    });
  }
  private cargarOpcionesSucursal() {
    const sucursales = this.sucursalService.sucursales();
    const restringidaId = this.sucursalService.sucursalRestringidaId();

    if (restringidaId != null) {
      const restringida = sucursales.find((s) => s.id === restringidaId);
      this.opcionesSucursal.set(
        restringida ? [{ label: restringida.nombre, value: restringida }] : [],
      );
      this.formFiltro.sucursal().value.set(restringida ?? null);
      return;
    }

    this.opcionesSucursal.set(
      sucursales.map((sucursal) => ({
        label: sucursal.nombre,
        value: sucursal,
      })),
    );
    this.opcionesSucursal.update((options) => [
      {
        label: 'Todas las sucursales',
        value: null,
      },
      ...options,
    ]);
  }
  private sucursalColumnTemplate = viewChild('sucursalColumnTemplate');
}
